import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import EllipsisIcon from 'assets/ellipsis.svg?react';
import { Context } from 'components/Store';
import { hasAccess } from 'utilities/access';
import './index.css';

// UI Kit replacement for components/EllipseMenu, which is built on grow-ui's
// ContextMenu. Same props, so call sites can swap one import for the other.
const ActionsMenu = ({
  onAction,
  options,
  disabled = false,
  portal = false
}) => {
  const [{ loggedInUserOrgPermission }] = useContext(Context);
  const { role, financialAccess } = loggedInUserOrgPermission ?? {};
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const listRef = useRef(null);
  const triggerRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const allowedOptions = useMemo(
    () =>
      options.filter(({ minAllowedRole, minFinancialAccess }) =>
        hasAccess(
          { role, financialAccess },
          { minAllowedRole, minFinancialAccess }
        )
      ),
    [options, role, financialAccess]
  );

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const handlePointerDown = event => {
      if (
        !containerRef.current.contains(event.target) &&
        !listRef.current?.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  React.useLayoutEffect(() => {
    if (!open || !portal) return undefined;
    const trigger = triggerRef.current.getBoundingClientRect();
    const list = listRef.current.getBoundingClientRect();
    setPosition({
      top:
        trigger.bottom + 6 + list.height > window.innerHeight
          ? Math.max(8, trigger.top - list.height - 6)
          : trigger.bottom + 6,
      left: Math.max(
        8,
        Math.min(trigger.right - list.width, window.innerWidth - list.width - 8)
      )
    });
    const closeOnScroll = event => {
      if (!listRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnResize = () => setOpen(false);
    window.addEventListener('scroll', closeOnScroll, true);
    window.addEventListener('resize', closeOnResize);
    return () => {
      window.removeEventListener('scroll', closeOnScroll, true);
      window.removeEventListener('resize', closeOnResize);
    };
  }, [open, portal]);

  // preventScroll matters here: without it the browser scrolls the freshly
  // focused option into view, which yanks the page as the menu opens.
  useEffect(() => {
    if (open) {
      listRef.current?.querySelector('button')?.focus({ preventScroll: true });
    }
  }, [open]);

  const closeAndRestoreFocus = () => {
    setOpen(false);
    triggerRef.current?.focus({ preventScroll: true });
  };

  const handleKeyDown = event => {
    const items = Array.from(listRef.current.querySelectorAll('button'));
    const index = items.indexOf(document.activeElement);

    if (event.key === 'Escape') {
      event.preventDefault();
      closeAndRestoreFocus();
      return;
    }

    // Tab walks the options and only leaves the menu past either end, so the
    // list behaves the way it looks rather than following raw DOM order.
    if (event.key === 'Tab') {
      const next = event.shiftKey ? index - 1 : index + 1;
      event.preventDefault();
      if (next < 0 || next >= items.length) {
        closeAndRestoreFocus();
      } else {
        items[next].focus({ preventScroll: true });
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      items[(index + 1) % items.length]?.focus({ preventScroll: true });
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      items[(index - 1 + items.length) % items.length]?.focus({
        preventScroll: true
      });
    }
  };

  if (!allowedOptions.length) {
    return null;
  }

  const menu = open && (
    <ul
      ref={listRef}
      className="actions-menu__list"
      style={
        portal
          ? {
              position: 'fixed',
              top: position.top,
              left: position.left,
              right: 'auto',
              zIndex: 1050,
              maxHeight: 'calc(100vh - 16px)',
              overflowY: 'auto'
            }
          : undefined
      }
      role="menu"
      data-testid="actions-menu-list"
      onKeyDown={handleKeyDown}
    >
      {allowedOptions.map(({ value, text, className }) => (
        <li key={value} role="none">
          <button
            type="button"
            role="menuitem"
            className={`actions-menu__option ${className ?? ''}`}
            onClick={event => {
              event.stopPropagation();
              setOpen(false);
              onAction(value);
            }}
          >
            {text}
          </button>
        </li>
      ))}
    </ul>
  );

  return (
    <div ref={containerRef} className="actions-menu">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Open actions menu"
        className="actions-menu__trigger"
        data-testid="actions-menu-trigger"
        disabled={disabled}
        onClick={event => {
          event.stopPropagation();
          setOpen(current => !current);
        }}
      >
        <EllipsisIcon width={24} height={24} />
      </button>

      {portal ? createPortal(menu, document.body) : menu}
    </div>
  );
};

ActionsMenu.propTypes = {
  onAction: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      className: PropTypes.string,
      minAllowedRole: PropTypes.string,
      minFinancialAccess: PropTypes.string
    })
  ).isRequired,
  disabled: PropTypes.bool,
  portal: PropTypes.bool
};

export default ActionsMenu;
